import React from 'react'
import { ZinButton } from '@tnl/zin-react'
import { FormError } from '@features/account/components/FormError/FormError'
import { useSignInContext } from '@features/account/hooks/useSignInContext'
import { initiateOktaPasswordReset } from '@hooks/useOkta'
import { useAutoClearTimer } from '@hooks/useAutoClearTimer'
import { SIGNIN_LABELS, RECOVERY_ERROR_CODES } from '@constants'
import { useAuthLabelsContext } from '@account/contexts/AuthLabelsContext'
import { SIGNIN_STEP_TO_BACKEND } from '@account/services/authLabels'
import type { SignInStepProps } from '../../types'
import './PasswordRecoveryEmailSentStep.scss'

export interface PasswordRecoveryEmailSentStepProps extends SignInStepProps {
  // email?: string
  // recoveryType: 'username' | 'password'
  // onResendEmail?: () => void
  // onCallUs?: () => void
}

export const PasswordRecoveryEmailSentStep: React.FC<PasswordRecoveryEmailSentStepProps> = ({
  // email,
  // recoveryType,
  // onResendEmail,
  // onCallUs,
  // isLoading = false,
  error,
  // stepIndex,
  // totalSteps,
  // validationErrors,
  errorHandling,
}) => {
  const { state } = useSignInContext()
  const { getLabel } = useAuthLabelsContext()
  const backendStep = SIGNIN_STEP_TO_BACKEND['forgot-password-reset-link'] ?? 'passwordRecoveryEmailSent'
  const [showResendWarning, setShowResendWarning] = React.useState(false)
  const [isResending, setIsResending] = React.useState(false)
  const [resendError, setResendError] = React.useState<string | undefined>(undefined)

  const resendWarningText = getLabel('signIn', backendStep, 'resendWarning', SIGNIN_LABELS.RESET_LINK_WARNING)
  const noUsernameForResendText = getLabel('signIn', backendStep, 'errors.noUsernameForResend', SIGNIN_LABELS.NO_USERNAME_FOR_RESEND)
  const bodyText = getLabel('signIn', backendStep, 'description', SIGNIN_LABELS.PASSWORD_RECOVERY_DESCRIPTION)
  const didntGetEmailHeading = getLabel('signIn', backendStep, 'didntGetEmail', SIGNIN_LABELS.DIDNT_GET_EMAIL)
  const resendEmailButtonLabel = getLabel('signIn', backendStep, 'resendEmail', SIGNIN_LABELS.RESEND_EMAIL)
  const callUsButtonLabel = getLabel('signIn', backendStep, 'callUs', SIGNIN_LABELS.CALL_US_PHONE)

  const {
    start: startTimer,
    stop: stopTimer,
    isActive,
  } = useAutoClearTimer({
    duration: 180000, // 3 minutes
    autoStart: false,
  })

  const handleResendEmail = async () => {
    if (isResending || isActive) return

    setIsResending(true)
    setResendError(undefined)
    setShowResendWarning(true)
    stopTimer()

    try {
      const username = state.recoveryUsername
      if (!username) {
        throw new Error(noUsernameForResendText || SIGNIN_LABELS.NO_USERNAME_FOR_RESEND)
      }

      await initiateOktaPasswordReset(username)
      setIsResending(false)
    } catch (err: unknown) {
      setIsResending(false)

      const message = err && typeof err === 'object' && 'message' in err ? (err as Error).message : String(err)
      const code = err && typeof err === 'object' && 'errorCode' in err ? (err as any).errorCode || '' : ''

      setResendError(message)

      // Start cooldown timer if rate limited
      if (code === RECOVERY_ERROR_CODES.OKTA_TOO_MANY_REQUESTS) {
        startTimer()
      }
    }
  }

  const handleCallUs = () => {
    window.location.assign(SIGNIN_LABELS.CALL_US_PHONE_LINK)
  }

  const getRecoveryText = () => bodyText || SIGNIN_LABELS.PASSWORD_RECOVERY_DESCRIPTION

  return (
    <div className="password-recovery-email-sent-step">
      {/* Error/Warning Panel */}
      {error && errorHandling?.apiErrors !== 'none' && <FormError errors={error.message ? [error.message] : []} />}

      {/* Resend Error Message */}
      {resendError && (
        <FormError
          message={resendError}
          type="error"
        />
      )}

      {/* Resend Warning Message */}
      {showResendWarning && !resendError && (
        <FormError
          message={resendWarningText || SIGNIN_LABELS.RESET_LINK_WARNING}
          type="warning"
          autoClear={{ enabled: true, duration: 30000 }}
        />
      )}

      {/* Rate Limit Warning */}
      {isActive && (
        <FormError
          message={resendWarningText || SIGNIN_LABELS.RESET_LINK_WARNING}
          type="warning"
        />
      )}

      {/* Body Text */}
      <div className="password-recovery-email-sent-step__body-text">
        <p className="body-2">{getRecoveryText()}</p>
      </div>

      {/* Button Section */}
      <div className="password-recovery-email-sent-step__button-section">
        <h3 className="subtitle-2">{didntGetEmailHeading || SIGNIN_LABELS.DIDNT_GET_EMAIL}</h3>

        <div className="password-recovery-email-sent-step__buttons">
          <ZinButton
            variant="primary"
            full
            onClick={handleResendEmail}
            loading={isResending}
            disabled={isActive}
            className="password-recovery-email-sent-step__resend">
            {resendEmailButtonLabel || SIGNIN_LABELS.RESEND_EMAIL}
          </ZinButton>

          <ZinButton
            variant="secondary"
            full
            onClick={handleCallUs}
            disabled={isResending}
            className="password-recovery-email-sent-step__callus">
            {callUsButtonLabel || SIGNIN_LABELS.CALL_US_PHONE}
          </ZinButton>
        </div>
      </div>
    </div>
  )
}

export default PasswordRecoveryEmailSentStep
